import React, { FCC, useState } from 'react'
import { Redirect, useLocation } from 'react-router'

import { routes } from '@sketch/modules-common'
import { ErrorHandler } from '@sketch/tracing'
import { useClearUrlQuery, useSearch } from '@sketch/utils'
import {
  DocumentHead,
  LoadingState,
  Pill,
  useForTablet,
  Tabs,
  Tab,
  TabList,
  TabPanel,
} from '@sketch/components'

// TODO; Given that we are removing DocumentItemsLayout, move this component
// to another place
import { Footer } from '../../../shares/components/DocumentItemsLayout/DocumentItemsLayout.styles'
import ProjectHeader from '../ProjectHeader'
import ProjectEmptyState from '../ProjectEmptyState'
import ProjectError, { ProjectNotFoundError } from '../ProjectError'
import {
  HeaderAfterCollections,
  DocumentsWrapper,
  TabPanelContent,
  MobileWrapper,
} from './ProjectShares.styles'

import { useProject } from '../../hooks/useProject/useProject'

import {
  WorkspaceMinimalFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'
import { ScrollToTop } from '../../../../components/ScrollToTopButton'
import NestedProjectItemsLayout from '../../../nestedProjects/components/NestedProjectItemsLayout'
import NestedProjectDropdown from '../../../nestedProjects/components/NestedProjectDropdown'
import { useStorageItemsFilters } from '../../../storageItems/hooks'
import { isProject, isShare } from '../../../storageItems/utils/filterTypes'
import { ProjectDocumentList } from './ProjectDocumentList'
import { useGetStashedProjectBreadcrumb } from '../../../storageItems/operations/useGetStashedProjectBreadcrumb'
import { PROJECTS_LAYOUT_COLUMNS } from '../../../storageItems/constants'
import { useProjectBanner } from '../../hooks/useProjectBanner'
import { GetStorageItemsProps } from '../../hooks/useProject'
import { AnimatedSelectionBottomSink } from '../../../storageItems/components/SelectionBottomSink'

const INITIAL_TAB = 0
const STORAGE_DEFAULT_TYPES = ['SHARE' as const, 'PROJECT' as const]

const getTypesForProjectQuery = (
  isMobile: boolean,
  tabState: number | null
) => {
  // On desktop we always want to show both shares and projects because they are listed together
  // On mobile, the initial load should always fetch both given that we don't know how many shares and projects there are, or if there are any
  // and on that case we hide the tab bar (if there's only one type of item )
  if (!isMobile || tabState === null) {
    // Defaulting to all types (["SHARE", "PROJECT"]) on the BE
    return STORAGE_DEFAULT_TYPES
  }

  // After a tab is selected we already know there's more than one type of item
  // so we can return the selected type
  if (tabState === 0) {
    return ['PROJECT' as const]
  }

  return ['SHARE' as const]
}

export interface ProjectSharesProps {
  projectId: string
  workspace: WorkspaceMinimalFragment
}

const ProjectShares: FCC<ProjectSharesProps> = props => {
  const { workspace, projectId } = props
  const location = useLocation()

  const [tabState, setTabState] = useState<number | null>(null)

  const { searchDebounced: search } = useSearch()
  const { filters, hasFilters: hasFilterTags } = useStorageItemsFilters()

  const hasFilters = hasFilterTags || !!search

  const { clearUrlQuery } = useClearUrlQuery()
  const handleClearFilters = () => {
    clearUrlQuery(['filter', 'filters', 'search'])
  }

  const isTabletAndBigger = useForTablet()
  const isMobile = !isTabletAndBigger

  const { identifier: workspaceId } = workspace

  const useProjectProps: GetStorageItemsProps = {
    workspaceId,
    projectId,
    search,
    filters,
    types: getTypesForProjectQuery(isMobile, tabState),
  }

  // The "initialQuery" will be used fetch the total count of storage items
  // and check (on mobile) if we should show the tab bar or not
  const initialQuery = useProject(useProjectProps)

  const queryVariables = {
    ...useProjectProps,
    types: STORAGE_DEFAULT_TYPES,
  }

  // If no tab is selected, this query data will match the one from the "initialQuery"
  // so no additional request will be made. However, we need to duplicate it work correctly
  // when selecting a tab
  const { data, loading, error, loadMore } = useProject(queryVariables)

  const project = data.project

  useProjectBanner({ project, workspaceId })

  const isStashedProject = data.project?.archivedAt || data.project?.deletedAt
  const shouldSkipStashedBreadcrumb = !isStashedProject

  const { breadcrumbs: breadcrumbsStashed } = useGetStashedProjectBreadcrumb(
    workspaceId,
    projectId,
    shouldSkipStashedBreadcrumb
  )

  // Load the document layout
  const { data: documentListSettingsData } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = documentListSettingsData || {}

  const isCurrentlyInProjectView =
    location.pathname ===
    routes.WORKSPACE_PROJECT.create({ workspaceId, projectId })

  const isCurrentlyInTrashProjectView =
    location.pathname ===
    routes.WORKSPACE_TRASH_PROJECT.create({ workspaceId, projectId })

  if (initialQuery.loading && !data.storageItems) {
    return <LoadingState />
  }

  const projectWorkspace = data.project?.workspace

  if (projectWorkspace && workspaceId !== projectWorkspace?.identifier) {
    return <ProjectNotFoundError />
  }

  if (error) {
    return <ProjectError projectId={projectId} error={error} />
  }

  const storageItems = data.storageItems
  const breadcrumbs = isStashedProject ? breadcrumbsStashed : data.breadcrumbs
  const nestedProjects = storageItems?.filter(isProject) || []
  const shares = storageItems?.filter(isShare) || []

  const { totalCount } = initialQuery
  const hasShares = totalCount.shares > 0
  const hasNestedProjects = totalCount.projects > 0

  /* ==========
   * Handle redirects when trashing / restoring projects
   * ==========
   * > Redirect to the Trash Project View if the project has been soft-deleted */

  if (!!project?.deletedAt && isCurrentlyInProjectView) {
    return (
      <Redirect
        to={routes.WORKSPACE_TRASH_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }

  /*
   * > Redirect to the Project View if the project was restored */
  if (!project?.deletedAt && isCurrentlyInTrashProjectView) {
    return (
      <Redirect
        to={routes.WORKSPACE_PROJECT.create({
          projectId,
          workspaceId,
        })}
        push={false}
      />
    )
  }
  /* ==========
   * ========== */

  // Project Empty State
  if (!loading && project && totalCount.storageItems === 0) {
    return (
      <ProjectEmptyState
        workspace={workspace}
        project={project}
        breadcrumbs={breadcrumbs}
        hasFilters={hasFilters}
        onClearFilters={handleClearFilters}
      />
    )
  }

  const renderDocumentsList = () => {
    if (!hasShares || !project) {
      return null
    }

    const isGridLayout = documentsLayout === 'GRID'

    return (
      <DocumentsWrapper
        listLayout={!isGridLayout}
        // return list without gradient styling when there's no nested projects
        grouped={hasNestedProjects}
      >
        {/**
         * This header should only be seen when the user has both shares and
         * nested projects and is on the grid view
         */}
        {hasNestedProjects && isGridLayout && isTabletAndBigger && (
          <HeaderAfterCollections>
            Documents
            <Pill variant="secondary">{totalCount.shares}</Pill>
          </HeaderAfterCollections>
        )}
        {/* NOTE: do not render ProjectDocumentList conditionally within or without a wrapper
         * as this might cause bugs. See more: https://linear.app/sketch/issue/SWEB-380/creating-project-causes-modal-to-close-prematurely
         */}
        <ProjectDocumentList
          project={project}
          workspace={workspace}
          items={shares}
          totalCount={totalCount.shares}
          onLoadMore={loadMore.storageItems}
          queryVariables={queryVariables}
        />
      </DocumentsWrapper>
    )
  }

  const renderNestedProjectsList = () => {
    if (!hasNestedProjects) return null

    return (
      <>
        <NestedProjectItemsLayout
          workspaceId={workspaceId}
          nestedProjects={nestedProjects}
          onLoadMore={loadMore.storageItems}
          totalCount={totalCount.projects}
          disabledDrag={
            Boolean(project?.deletedAt) ||
            !(workspace.userCanEdit || workspace.userCanAdminister)
          }
          renderDropdown={item => {
            const nestedProject = nestedProjects.find(
              p => p.identifier === item.identifier
            )

            if (!nestedProject) {
              ErrorHandler.shouldNeverHappen(
                'share should always be present rendering the ProjectDocumentDropdown'
              )
              return null
            }

            return (
              <NestedProjectDropdown
                workspace={workspace}
                nestedProject={nestedProject}
              />
            )
          }}
          extraPlaceholders={
            // In case we haven't reached the documents list, but we have exhausted the nested projects list
            // we want to add additional placeholders to the end of the list, so they would trigger the loading
            //
            // This is crucial when projects list length matches with the pagination length multiple.
            // E.g. if the pagination length is 20 and we have 20, 40, 60, etc. projects,
            hasShares ? 0 : totalCount?.storageItems - nestedProjects.length
          }
          customColumns={PROJECTS_LAYOUT_COLUMNS}
        />
        <ScrollToTop />
      </>
    )
  }

  // For mobile view we want to show tabs
  if (isMobile && hasNestedProjects) {
    return (
      <MobileWrapper>
        <DocumentHead title={project?.name} />
        {project && (
          <ProjectHeader
            project={project}
            workspace={workspace}
            breadcrumbs={breadcrumbs}
            workspaceAccessLevel={project?.workspaceAccessLevel!}
          />
        )}
        <Tabs
          selectedTabIndex={tabState || INITIAL_TAB}
          onTabSelected={setTabState}
        >
          <TabList>
            <Tab>
              Folders{' '}
              {totalCount.projects && (
                <Pill variant="secondary">{totalCount.projects}</Pill>
              )}
            </Tab>
            <Tab>
              Documents{' '}
              {totalCount.shares && (
                <Pill variant="secondary">{totalCount.shares}</Pill>
              )}
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelContent>
              {renderNestedProjectsList()}
              <Footer />
            </TabPanelContent>
          </TabPanel>
          <TabPanel>
            <TabPanelContent>{renderDocumentsList()}</TabPanelContent>
          </TabPanel>
        </Tabs>
      </MobileWrapper>
    )
  }

  return (
    <>
      <DocumentHead title={project?.name} />
      {project && (
        <ProjectHeader
          project={project}
          workspace={workspace}
          breadcrumbs={breadcrumbs}
          workspaceAccessLevel={project?.workspaceAccessLevel!}
        />
      )}
      {renderNestedProjectsList()}
      {renderDocumentsList()}

      <AnimatedSelectionBottomSink
        workspaceIdentifier={workspace.identifier}
        project={project}
      />
      <Footer />
    </>
  )
}

export default ProjectShares
